/**
 * Componente riutilizzabile per abilitare/disabilitare le notifiche push.
 * Da inserire nella pagina Calendario (o Profilo). Mostra info specifiche per iOS.
 */
import React, { useEffect, useState, useCallback } from "react";
import { Bell, BellOff, BellRing, Smartphone } from "lucide-react";
import { toast } from "sonner";
import { getPushState, requestPushPermission, unsubscribePush } from "@/lib/push";

export default function PushNotificationToggle({ compact = false }) {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    const s = await getPushState();
    setState(s);
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const handleEnable = async () => {
    setBusy(true);
    try {
      const res = await requestPushPermission();
      if (res.ok) {
        toast.success("Notifiche abilitate su questo dispositivo");
        await refresh();
      } else if (res.reason === "ios-needs-install") {
        toast.error(
          "Su iPhone devi prima installare l'app: apri in Safari → Condividi → Aggiungi a Home",
          { duration: 8000 }
        );
      } else if (res.reason === "denied") {
        toast.error("Permesso negato. Abilita le notifiche dalle impostazioni del browser.");
      } else {
        toast.error("Notifiche non supportate su questo browser");
      }
    } catch (e) {
      toast.error("Errore: " + (e?.message || e));
    } finally {
      setBusy(false);
    }
  };

  const handleDisable = async () => {
    setBusy(true);
    try {
      await unsubscribePush();
      toast.success("Notifiche disabilitate");
      await refresh();
    } finally {
      setBusy(false);
    }
  };

  if (!state) return null;

  // iOS Safari non-installato: mostra istruzioni
  if (state.iosNeedsInstall) {
    return (
      <div
        data-testid="push-ios-install-hint"
        className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-amber-50 border border-amber-200 text-amber-900 text-xs"
      >
        <Smartphone className="w-4 h-4 flex-shrink-0" />
        <span>
          iPhone: apri in Safari → <b>Condividi</b> → <b>Aggiungi a Home</b> per ricevere notifiche
        </span>
      </div>
    );
  }

  if (!state.supported) {
    return (
      <div
        data-testid="push-unsupported"
        className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-slate-100 border border-slate-200 text-slate-600 text-xs"
      >
        <BellOff className="w-4 h-4" /> Notifiche non supportate
      </div>
    );
  }

  if (state.subscribed) {
    return (
      <button
        data-testid="push-disable-btn"
        onClick={handleDisable}
        disabled={busy}
        className={`inline-flex items-center gap-2 px-3 py-2 rounded-md border transition ${
          compact ? "text-xs" : "text-sm"
        } bg-emerald-50 border-emerald-200 text-emerald-800 hover:bg-emerald-100 disabled:opacity-60`}
      >
        <BellRing className="w-4 h-4" />
        {compact ? "Attive" : "Notifiche attive · disattiva"}
      </button>
    );
  }

  return (
    <button
      data-testid="push-enable-btn"
      onClick={handleEnable}
      disabled={busy || state.permission === "denied"}
      className={`inline-flex items-center gap-2 px-3 py-2 rounded-md border transition ${
        compact ? "text-xs" : "text-sm"
      } bg-blue-600 text-white border-blue-700 hover:bg-blue-700 disabled:opacity-60`}
    >
      <Bell className="w-4 h-4" />
      {state.permission === "denied"
        ? "Notifiche bloccate dal browser"
        : "Attiva notifiche calendario"}
    </button>
  );
}
